import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { SpriteIcon } from '@/components/SpriteIcon';
import { AuthCard, AuthErrorSummary } from './AuthCard';
import { AuthField } from './AuthField';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';
import { SubmitButton } from './SubmitButton';
import { changePasswordSchema, PASSWORD_MIN_LENGTH, type ChangePasswordValues } from './schemas';
import { useServerValidation } from './useServerValidation';

const FIELDS = ['currentPassword', 'newPassword', 'confirmPassword'] as const;

/**
 * Replaces `Pages/Auth/ChangePassword.cshtml`.
 *
 * Two variants of one screen, as before: a voluntary change, and the mandatory
 * one an administrator can force. The mandatory variant swaps the subtitle for a
 * warning and drops the Cancel button and the security tip, because there is
 * nowhere to cancel to — `RequireAuth` sends the user straight back here.
 *
 * `mustChangePassword` comes from `/me` rather than from a flag passed in, so it
 * stays right if the change fails: the account is still blocked, and the screen
 * still has to hide Cancel. `refresh()` after a success is what lets the guard
 * release the rest of the app.
 */
export function ChangePasswordPage() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const isMandatory = user?.mustChangePassword ?? false;

  const schema = useMemo(() => changePasswordSchema(t), [t]);
  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const { formErrors, clearFormErrors, applyServerErrors } = useServerValidation(setError, FIELDS);

  // Feed the strength meter and the live match indicator, both of which the
  // Razor page drove from `input` listeners.
  const newPassword = useWatch({ control, name: 'newPassword' });
  const confirmPassword = useWatch({ control, name: 'confirmPassword' });

  const onSubmit = handleSubmit(async (values) => {
    clearFormErrors();

    try {
      await api.post<void>('/api/auth/change-password', values);
      // Refresh before navigating: while mustChangePassword is still cached as
      // true, the guard would bounce any destination straight back here.
      await refresh();
      navigate('/', { replace: true });
    } catch (error) {
      applyServerErrors(error);
    }
  });

  return (
    <AuthCard
      aboveTitle={
        isMandatory ? (
          <div className="alert alert-warning mb-3" role="alert">
            <div className="d-flex align-items-center justify-content-center">
              <SpriteIcon name="alert-triangle" size={20} className="sa-icon me-2" />
              <strong>{t('auth:Auth.ChangePasswordRequired', 'Password Change Required')}</strong>
            </div>
            <p className="mb-0 mt-2 small">
              {t(
                'auth:Auth.PasswordChangeRequiredMessage',
                'You must change your password before you can continue.',
              )}
            </p>
          </div>
        ) : null
      }
      title={
        isMandatory
          ? t('auth:Auth.UpdateYourPassword', 'Update Your Password')
          : t('auth:Auth.ChangePassword', 'Change Password')
      }
      subtitle={isMandatory ? undefined : t('auth:Auth.UpdateAccountPassword', 'Update your account password')}
      footer={
        isMandatory ? null : (
          <div className="text-center mt-4">
            {/* Flex row because the theme sets `.sa-icon { display: block }`, which
                otherwise drops the icon onto its own line, left of the text. */}
            <p className="text-white-50 small d-flex align-items-center justify-content-center">
              <SpriteIcon name="shield" size={14} className="sa-icon me-1" />
              {t(
                'auth:Auth.PasswordSecurityTip',
                'Use a unique password you do not use anywhere else.',
              )}
            </p>
          </div>
        )
      }
    >
      <AuthErrorSummary errors={formErrors} />

      <form onSubmit={onSubmit} noValidate>
        <AuthField
          {...register('currentPassword')}
          label={t('auth:Auth.CurrentPassword', 'Current Password')}
          type="password"
          toggle
          autoComplete="current-password"
          autoFocus
          placeholder={t('auth:Auth.EnterCurrentPassword', 'Enter your current password')}
          error={errors.currentPassword?.message}
        />

        <AuthField
          {...register('newPassword')}
          label={t('auth:Auth.NewPassword', 'New Password')}
          type="password"
          toggle
          autoComplete="new-password"
          placeholder={t('auth:Auth.EnterNewPassword', 'Enter your new password')}
          hint={t(
            'auth:Auth.PasswordRequirements',
            'At least {{minLength}} characters, including upper and lower case, a number and a symbol.',
            { minLength: PASSWORD_MIN_LENGTH },
          )}
          error={errors.newPassword?.message}
        />

        <PasswordStrengthMeter password={newPassword ?? ''} />

        <div className="mt-3">
          <AuthField
            {...register('confirmPassword')}
            label={t('auth:Auth.ConfirmNewPassword', 'Confirm New Password')}
            type="password"
            toggle
            autoComplete="new-password"
            placeholder={t('auth:Auth.ReEnterPassword', 'Re-enter your new password')}
            state={
              confirmPassword ? (confirmPassword === newPassword ? 'valid' : 'invalid') : undefined
            }
            error={errors.confirmPassword?.message}
          />
        </div>

        <div className="d-grid gap-2">
          <SubmitButton
            icon="check"
            label={t('auth:Auth.UpdatePassword', 'Update Password')}
            pending={isSubmitting}
          />
        </div>

        {!isMandatory && (
          <div className="d-grid gap-2 mt-2">
            <Link to="/" className="btn btn-outline-secondary">
              {t('Cancel', 'Cancel')}
            </Link>
          </div>
        )}
      </form>
    </AuthCard>
  );
}
